import { pushEvent } from './gtm';

// Fonction pour gérer les événements de navigation principale
export const handleMainNavEvent = (section) => {
    pushEvent('main_nav', { section });
};

// Fonction pour gérer les événements de sous-navigation
export const handleSubNavEvent = (section, chapter) => {
    pushEvent('sub_nav', { section, chapter });
};

// Fonction pour gérer les actions dans la sous-navigation
export const handleSubNavActionEvent = (section, chapter, action, part = null) => {
    const params = { section, chapter, action };
    if (part) {
        params.part = part;
    }
    pushEvent('sub_nav_action', params);
};

// Fonction pour mapper les anciens événements ReactGA aux nouveaux événements GTM
export const mapLegacyEvent = (category, action, label = null, value = null) => {
    // Mappez les événements en fonction de leur contenu
    if (category === "Module" && action === "click") {
        // Un clic sur un module correspond à une sous-navigation, PAS à la navigation principale
        // Éviter d'émettre 'main_nav' ici pour ne pas dupliquer avec 'sub_nav'
        if (label) {
            const { sectionFromLabel, chapterFromLabel } = parseLabel(label);
            handleSubNavEvent(sectionFromLabel, chapterFromLabel);
        }
    } else if (category === "Button" && action === "click") {
        // Mapper les événements de bouton (format attendu: "Section - Chapitre - Action ...")
        const { sectionFromLabel, chapterFromLabel, actionFromLabel } = parseLabel(label);
        handleSubNavActionEvent(sectionFromLabel, chapterFromLabel, actionFromLabel || 'click');
    } else if (category === "VideoList") {
        // Mapper les événements de la liste vidéo vers les schémas GA4 attendus
        // action peut être "Video - <Categorie>" ou "back_to_categories"
        if (action && action.startsWith('Video - ')) {
            const chapterName = action.replace('Video - ', '').trim();
            if (label === 'part' && value) {
                // Visionnage/chargement d'une vidéo spécifique
                handleSubNavActionEvent('L&D Center', chapterName, 'video_view', value);
            } else {
                // Sélection d'une catégorie vidéo
                handleSubNavEvent('L&D Center', chapterName);
            }
        } else if (action === 'back_to_categories') {
            handleSubNavActionEvent('L&D Center', 'Videos', 'back_to_categories');
        } else {
            pushCustom(category, action, label, value);
        }
    } else if (isLdCenterCategory(category) && action === 'click') {
        // Clic sur une vidéo dans une catégorie (VideoList) → action structurée
        handleSubNavActionEvent('L&D Center', category, 'video_select', label || undefined);
    } else {
        pushCustom(category, action, label, value);
    }
};

// Fonction utilitaire pour déterminer la section à partir d'un label
function determineSectionFromLabel(label) {
    if (!label) return 'L&D Center';
    const normalized = label.toLowerCase();
    if (normalized.includes('technology')) return 'Technology Hub';
    if (normalized.includes('optometry')) return 'Optometry Lab';
    if (normalized.includes('service')) return 'Service Center';
    if (normalized.includes('l&d')) return 'L&D Center';
    // Heuristique via mots-clés
    if (["lucidis toric", "lucidis", "eden toric", "eden", "instant focus", "quality certificate", "map"].some(k => normalized.includes(k))) {
        return 'Technology Hub';
    }
    if (["snellen", "products", "biometer", "oct", "otc"].some(k => normalized.includes(k))) {
        return 'Optometry Lab';
    }
    if (["contact us", "toric calculator", "patient door", "patient door access", "new service available", "event request", "optimized selection"].some(k => normalized.includes(k))) {
        return 'Service Center';
    }
    if (["medical conference", "training videos", "patient glossary", "articles"].some(k => normalized.includes(k))) {
        return 'L&D Center';
    }
    return 'Service Center';
}

// Fonction utilitaire pour déterminer le chapitre à partir d'un label
function determineChapterFromLabel(label) {
    if (!label) return '';
    const parts = label.split(' - ');
    if (parts.length > 1) return parts[1];
    const normalized = label.toLowerCase();
    const mapping = [
        { key: 'instant focus', value: 'Instant Focus' },
        { key: 'quality certificate', value: 'Quality Certificate' },
        { key: 'snellen', value: 'Snellen' },
        { key: 'products', value: 'Products' },
        { key: 'biometer', value: 'Biometer' },
        { key: 'oct', value: 'OCT' },
        { key: 'otc', value: 'OCT' },
        { key: 'toric calculator', value: 'Toric Calculator' },
        { key: 'patient door access', value: 'Patient Door' },
        { key: 'patient door', value: 'Patient Door' },
        { key: 'new service', value: 'New Service available' },
        { key: 'event request', value: 'Event Request' },
        { key: 'optimized selection', value: 'Optimized Selection' },
        { key: 'medical conference', value: 'Medical Conference' },
        { key: 'training videos', value: 'Training Videos' },
        { key: 'patient glossary', value: 'Patient Glossary' },
        { key: 'articles', value: 'Scientific Publication' }
    ];
    const found = mapping.find(m => normalized.includes(m.key));
    if (found) return found.value;
    return label;
}

// Parse un label au format "Section - Chapitre - Action ..."
function parseLabel(label) {
    const safe = label || '';
    const tokens = safe.split(' - ').map(t => t.trim()).filter(Boolean);
    let sectionFromLabel = determineSectionFromLabel(safe);
    let chapterFromLabel = determineChapterFromLabel(safe);
    let actionFromLabel = undefined;
    if (tokens.length >= 3) {
        sectionFromLabel = tokens[0];
        chapterFromLabel = tokens[1];
        actionFromLabel = tokens.slice(2).join(' - ');
    }
    return { sectionFromLabel, chapterFromLabel, actionFromLabel };
}

function pushCustom(category, action, label, value) {
    const paramsExt = { category, action };
    if (value && label) {
        paramsExt[label] = value;
    } else if (label && !value) {
        paramsExt.label = label;
    }
    pushEvent('custom_event', paramsExt);
}

// Catégories L&D connues pour mapper les clics vidéo
function isLdCenterCategory(category) {
    if (!category) return false;
    const known = new Set([
        'Base Training',
        'Toric Calculator Training',
        'Clinical Case Training',
        'Glossary',
        'Dr. Vasilis S.Liarakos',
        'Dr. Hayat A.Khan',
        'Dr. Yehia Salah',
        'Dr. Wafi Mayhoub',
        'Dr. Diego Vezzola',
        'Dr. Yehia Salah - ESOIRS 2025',
        'Dr. Vaishal Kenia and Dr. Kamal Kapur'
    ]);
    return known.has(category);
}